import { ReactNode, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { ContactsTab } from "@/components/DealerDetail/ContactsTab";
import { DealsTab } from "@/components/DealerDetail/DealsTab";
import { NotesTab } from "@/components/DealerDetail/NotesTab";
import { RatesTab } from "@/components/DealerDetail/RatesTab";
import { StockFeedTab } from "@/components/DealerDetail/StockFeedTab";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi } from "@/hooks/useApi";
import { formatCurrency, formatDate } from "@/lib/utils";
import { dealerService } from "@/services/DealerService";
import { DealerCompanyDetails, DealerOverview } from "@/types/dealer";

const displayValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

function DetailField({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="min-w-0 border-b py-2 last:border-b-0">
      <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
      <dd className="mt-0.5 break-words text-sm">{displayValue(value)}</dd>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      <dl className="grid grid-cols-1 gap-x-6 sm:grid-cols-2 xl:grid-cols-3">{children}</dl>
    </section>
  );
}

const formatAddress = (details: DealerCompanyDetails, prefix: "site" | "billing" | "registered"): string => {
  const title = `${prefix[0].toUpperCase()}${prefix.slice(1)}` as "Site" | "Billing" | "Registered";
  const parts = [
    details[`${prefix}SubBuilding` as `${typeof prefix}SubBuilding`],
    details[`${prefix}BuildingName` as `${typeof prefix}BuildingName`],
    details[`${prefix}BuildingNumber` as `${typeof prefix}BuildingNumber`],
    details[`${prefix}StreetName` as `${typeof prefix}StreetName`],
    details[`${prefix}Locality` as `${typeof prefix}Locality`],
    details[`${prefix}Town` as `${typeof prefix}Town`],
    details[`${prefix}Postcode` as `${typeof prefix}Postcode`],
  ].filter((value): value is string => Boolean(value?.trim()));
  return parts.length > 0 ? parts.join(", ") : `No ${title.toLowerCase()} address available`;
};

function DetailsTab({ dealer }: { dealer: DealerOverview }) {
  const { summary, companyDetails } = dealer;

  return (
    <div className="space-y-6 rounded-md border p-4">
      <DetailSection title="Status and ownership">
        <DetailField label="Status" value={summary.status} />
        <DetailField label="Status reason" value={summary.statusReason} />
        <DetailField label="Car search status" value={summary.carSearchStatus} />
        <DetailField label="Intro status" value={summary.introStatus} />
        <DetailField label="Personal guarantee" value={summary.personalGuaranteeStatus} />
        <DetailField label="Relationship manager" value={summary.relationshipManager} />
        <DetailField label="Area sales manager" value={summary.areaSalesManager} />
        <DetailField label="Trusted" value={summary.isTrusted ? "Yes" : "No"} />
        <DetailField label="First deal" value={summary.firstDeal ? formatDate(summary.firstDeal) : null} />
        <DetailField label="Last deal" value={summary.lastDeal ? formatDate(summary.lastDeal) : null} />
        <DetailField label="Deals" value={summary.dealsCount} />
        <DetailField label="Admin fee" value={summary.adminFee === null ? null : formatCurrency(summary.adminFee)} />
      </DetailSection>

      <DetailSection title="Company details">
        <DetailField label="Company name" value={companyDetails.name} />
        <DetailField label="Trading name" value={companyDetails.tradingName} />
        <DetailField label="Type" value={companyDetails.type} />
        <DetailField label="Dealer type" value={companyDetails.dealerType} />
        <DetailField label="Dealer group" value={companyDetails.dealerGroup} />
        <DetailField
          label="Incorporated"
          value={companyDetails.incorporatedDate ? formatDate(companyDetails.incorporatedDate) : null}
        />
        <DetailField label="Company number" value={companyDetails.companyNumber} />
        <DetailField label="VAT number" value={companyDetails.vatNumber} />
        <DetailField label="FCA number" value={companyDetails.fcaNumber} />
        <DetailField label="FCA status" value={companyDetails.fcaStatus} />
        <DetailField label="ICO number" value={companyDetails.icoNumber} />
        <DetailField
          label="ICO expiry"
          value={companyDetails.icoExpiryDate ? formatDate(companyDetails.icoExpiryDate) : null}
        />
      </DetailSection>

      <DetailSection title="Contact channels">
        <DetailField label="Main phone" value={companyDetails.mainPhoneNumber} />
        <DetailField label="Secondary phone" value={companyDetails.secondaryPhoneNumber} />
        <DetailField label="Main email" value={companyDetails.mainEmail} />
        <DetailField label="Sales email" value={companyDetails.salesEmail} />
        <DetailField label="Billing email" value={companyDetails.billingEmail} />
        <DetailField label="Commission email" value={companyDetails.commissionEmail} />
        <DetailField label="Website" value={companyDetails.webSiteUrl} />
      </DetailSection>

      <DetailSection title="Addresses">
        <DetailField label="Site address" value={formatAddress(companyDetails, "site")} />
        <DetailField label="Billing address" value={formatAddress(companyDetails, "billing")} />
        <DetailField label="Registered address" value={formatAddress(companyDetails, "registered")} />
      </DetailSection>

      <DetailSection title="Credit information">
        <DetailField label="CreditSafe status" value={companyDetails.creditSafeStatus} />
        <DetailField label="Credit rating" value={companyDetails.creditRating} />
        <DetailField
          label="Net worth"
          value={companyDetails.netWorth === null ? null : formatCurrency(companyDetails.netWorth)}
        />
        <DetailField
          label="Credit limit"
          value={companyDetails.creditLimit === null ? null : formatCurrency(companyDetails.creditLimit)}
        />
        <DetailField label="CCJ count" value={companyDetails.ccjCount} />
        <DetailField
          label="CCJ value"
          value={companyDetails.ccjValue === null ? null : formatCurrency(companyDetails.ccjValue)}
        />
      </DetailSection>
    </div>
  );
}

const lazyTabs = [
  { value: "notes", label: "Notes", Component: NotesTab },
  { value: "contacts", label: "Contacts", Component: ContactsTab },
  { value: "deals", label: "Deals", Component: DealsTab },
  { value: "rates", label: "Rates", Component: RatesTab },
  { value: "stock-feed", label: "Stock Feed", Component: StockFeedTab },
];

// Tabs load on first visit and stay mounted, so their data is cached until the user leaves this dealer.
function DealerTabs({ dealer }: { dealer: DealerOverview }) {
  const [activeTab, setActiveTab] = useState("details");
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set(["details"]));

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setVisitedTabs((previous) => (previous.has(value) ? previous : new Set(previous).add(value)));
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="min-w-0">
      <div className="overflow-x-auto pb-1">
        <TabsList className="min-w-max">
          <TabsTrigger value="details">Details</TabsTrigger>
          {lazyTabs.map(({ value, label }) => (
            <TabsTrigger key={value} value={value}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      <TabsContent value="details">
        <DetailsTab dealer={dealer} />
      </TabsContent>
      {lazyTabs.map(({ value, Component }) =>
        visitedTabs.has(value) ? (
          <TabsContent key={value} value={value} forceMount className="data-[state=inactive]:hidden">
            <Component dealerId={dealer.id} />
          </TabsContent>
        ) : null,
      )}
    </Tabs>
  );
}

export function DealerDetail() {
  const { dealerId } = useParams<{ dealerId: string }>();
  const parsedDealerId = Number(dealerId);
  const hasValidDealerId = Boolean(dealerId) && Number.isInteger(parsedDealerId) && parsedDealerId > 0;
  const { data: dealer, loading, error } = useApi(
    (signal) => dealerService.getDealerOverview(parsedDealerId, signal),
    [parsedDealerId],
    { skip: !hasValidDealerId },
  );

  return (
    <div className="flex h-full min-w-0 flex-col gap-4 overflow-y-auto px-4 py-3">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/dealer-search">
            <ChevronLeft className="size-4" />
            Back to dealer search
          </Link>
        </Button>
      </div>

      {!hasValidDealerId && (
        <div className="border-destructive text-destructive rounded-md border p-3 text-sm" role="alert">
          Invalid dealer ID.
        </div>
      )}

      {hasValidDealerId && loading && <p className="text-muted-foreground text-sm">Loading dealer details...</p>}

      {hasValidDealerId && error && (
        <div className="border-destructive text-destructive rounded-md border p-3 text-sm" role="alert">
          {error}
        </div>
      )}

      {hasValidDealerId && !loading && !error && !dealer && (
        <p className="text-muted-foreground rounded-md border p-3 text-sm">Dealer details were not found.</p>
      )}

      {dealer && (
        <>
          <header className="border-b pb-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h1 className="text-2xl font-semibold">{dealer.name}</h1>
                <p className="text-muted-foreground text-sm">Dealer ID {dealer.id}</p>
              </div>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-4">
                <DetailField label="Status" value={dealer.summary.status || dealer.summary.carSearchStatus} />
                <DetailField label="Trusted" value={dealer.summary.isTrusted ? "Yes" : "No"} />
                <DetailField label="Deals" value={dealer.summary.dealsCount} />
                <DetailField label="Relationship manager" value={dealer.summary.relationshipManager} />
              </dl>
            </div>
          </header>

          <DealerTabs key={dealer.id} dealer={dealer} />
        </>
      )}
    </div>
  );
}